import { useState } from "react";

import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";

import {
  MAX_LOOKUP_LENGTH,
  MIN_LOOKUP_LENGTH,
  useAttendeeLookup,
  type LookupPerson,
} from "../use-attendee-lookup";

/** What the server returns at most, so a full list is worth a hint. */
const MAX_PEOPLE = 8;

/**
 * "Find someone already on the app", above the Add attendee form.
 *
 * Searching by name or email is a backend lookup (use-attendee-lookup.ts),
 * because organisers cannot read other people's profiles. Choosing a person
 * only fills Name, Email and Company in the form below; saving still files the
 * ordinary ADD_EVENT_ATTENDEE command. Typing the details by hand stays as it
 * was, for people who have not signed in yet.
 */
export function AttendeeFinder({
  eventId,
  uid,
  disabled,
  onPick,
}: {
  eventId: string;
  uid: string;
  disabled?: boolean;
  onPick: (fields: { name: string; email: string; company: string }) => void;
}) {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<LookupPerson | null>(null);
  const { state, retry } = useAttendeeLookup(eventId, uid, query);
  const length = query.trim().length;
  // `state` catches up with the text one render later; until it does it still
  // describes the previous query.
  const current =
    length >= MIN_LOOKUP_LENGTH ? state : { status: "idle" as const };

  function choose(person: LookupPerson) {
    onPick({
      name: person.name || person.email,
      email: person.email,
      company: person.company,
    });
    setPicked(person);
    setQuery("");
  }

  return (
    <div className="space-y-2" data-testid="attendee-finder">
      <Label htmlFor="attendee-finder-search">
        Find someone already on the app
      </Label>
      <Input
        id="attendee-finder-search"
        testId="attendee-finder-search"
        type="text"
        inputMode="search"
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        maxLength={MAX_LOOKUP_LENGTH}
        placeholder="Name or email"
        aria-describedby="attendee-finder-status"
        disabled={disabled}
        value={query}
        onChange={(event) => {
          setPicked(null);
          setQuery(event.target.value);
        }}
      />

      <div
        id="attendee-finder-status"
        role="status"
        aria-live="polite"
        className="text-sm text-muted-foreground"
        data-testid="attendee-finder-status"
      >
        {length === 0 && !picked && (
          <p>
            Type at least {MIN_LOOKUP_LENGTH} letters of a name or an email
            address. Not on the app yet? Fill in the details below instead.
          </p>
        )}
        {length === 0 && picked && (
          <p data-testid="attendee-finder-picked">
            Filled in from {picked.name || picked.email}. Check the details
            below, then add them.
          </p>
        )}
        {length > 0 && length < MIN_LOOKUP_LENGTH && (
          <p data-testid="attendee-finder-short">
            Type at least {MIN_LOOKUP_LENGTH} characters.
          </p>
        )}
        {current.status === "searching" && (
          <p data-testid="attendee-finder-searching">Searching…</p>
        )}
        {current.status === "done" && current.people.length === 0 && (
          <p data-testid="attendee-finder-empty">
            No one found. They may not have signed in yet, or they are already
            on this list. You can fill in their details below instead.
          </p>
        )}
        {current.status === "done" && current.people.length > 0 && (
          <p data-testid="attendee-finder-count">
            {current.people.length === 1
              ? "1 person found."
              : `${current.people.length} people found.`}
          </p>
        )}
        {/* The one announcement of a failure: the region is polite, so a
            second role="alert" inside it would read the message twice. */}
        {current.status === "error" && (
          <p
            className="text-destructive-text"
            data-testid="attendee-finder-error"
          >
            {current.message}
          </p>
        )}
      </div>

      {current.status === "error" && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-h-11"
          data-testid="attendee-finder-retry"
          onClick={retry}
        >
          Try again
        </Button>
      )}

      {current.status === "done" && current.people.length > 0 && (
        <>
          <ul className="space-y-1.5" data-testid="attendee-finder-results">
            {current.people.map((person) => (
              <li key={person.id}>
                <button
                  type="button"
                  disabled={disabled}
                  data-testid={`attendee-finder-option-${person.id}`}
                  onClick={() => choose(person)}
                  className="flex min-h-11 w-full min-w-0 flex-col items-start justify-center rounded-lg border border-border px-3 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring/30 disabled:opacity-50"
                >
                  <span className="w-full truncate font-medium">
                    {person.name || person.email}
                  </span>
                  {(person.company || person.name) && (
                    <span className="w-full text-muted-foreground wrap-anywhere">
                      {[person.company, person.name ? person.email : ""]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          {current.people.length >= MAX_PEOPLE && (
            <p
              className="text-sm text-muted-foreground"
              data-testid="attendee-finder-more"
            >
              Showing the first {MAX_PEOPLE}. Type more to narrow it down.
            </p>
          )}
        </>
      )}
    </div>
  );
}
